// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2024-09-23 00:53:53 +0200 (Mon, 23 Sep 2024) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 146083 $
//  Purpose     : templating sagas
// =============================================================================
// ---Third-party imports
import React, {Component} from 'react'
import now from 'performance-now'
import {Label, Icon, Button} from 'semantic-ui-react'
import clsx from 'clsx'
import PropTypes from 'prop-types'

// ---Framework imports
import {map, filterItems, dict, size, callIfFunc} from 'ldash'
import connect from 'uix/connect'
import {decodeGo} from 'uix/templating'
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import styles from 'uix/styles'
import {ensurePathPrefix} from 'uix/routing'
import {useState, useApi, useSelector} from 'uix/hooks'
import {VIcon} from 'graphics'

// ---Globals
const LEGACY_CLASSES = ['nav']
const decodeGoLazy = (
  isLazyPending,
  request,
  oldRequest,
  replacedQueryParams,
  domains,
  templatingcfgs
) => {
  if (isLazyPending) {
    return {pathname: ''}
  } else {
    oldRequest = {...oldRequest, query: {...oldRequest.query, ...replacedQueryParams}}
    return decodeGo(request, oldRequest, domains, templatingcfgs)
  }
}

class Link extends Component {
  static defaultProps = {
    // direct path fed into go action
    path: '',
    // go action compatible request (dname, classname, tabname, id, ...)
    request: {},
    // merge templating params into request
    dname: undefined,
    odid: undefined,
    classname: undefined,
    title: null,
    tabname: undefined,
    ctxid: undefined, // nulls are allowed values here
    id: undefined, // nulls are allowed values here
    query: undefined, // set query string
    updateQuery: undefined, // update query string
    // react children elements - probably a text node
    children: null,
    // css related
    cut: false,
    // render only span
    disabled: false,
    // inline css
    style: undefined,
    // defer domain initialization to first hover
    lazy: false,
    // open in new tab
    external: false,
    // external link target if "path" is given
    // overwrite with null for open in current tab
    target: '_new',
  }

  static propTypes = {
    domains: PropTypes.object.isRequired,
  }

  static storeMap = (root, props) => ({
    oldRequest: root.meta.request,
    replacedQueryParams: root.meta.replacedQueryParams,
    templatingcfgs: root.config.templating,
    domains: root.config.domains,
    loading: root.config.loading,
    deploy: root.config.deploy,
    api_offline: root.meta.state == 'API_OFFLINE',
    app_running: root.meta.state == 'APP_RUNNING',
    apiurl: Sel.apiurl(root),
  })

  static actionMap = {go: Actions.go, initDomain: Actions.initDomain}

  state = {hovered: false}

  static mergeProps(request, props) {
    let {dname, odid, classname, tabname, ctxid, id, query, updateQuery} = props
    let mergeRequest = dict(
      filterItems(
        {dname, odid, classname, tabname, ctxid, id, query, updateQuery},
        (k, v) => v !== undefined
      )
    )
    return {...request, ...mergeRequest}
  }

  static is_disabled = props => {
    let {disabled, api_offline, app_running} = props
    return disabled || api_offline || !app_running
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let {
      request,
      oldRequest,
      replacedQueryParams,
      domains,
      templatingcfgs,
      initDomain,
      path,
      loading,
    } = nextProps
    if (!domains) {
      return null
    }
    let disabled = Link.is_disabled(nextProps)
    if (!path && !disabled) {
      try {
        request = Link.mergeProps(request, nextProps)
        let isLazyPending = nextProps.lazy && !prevState.hovered
        var decoded = decodeGoLazy(
          isLazyPending,
          request,
          oldRequest,
          replacedQueryParams,
          domains,
          templatingcfgs
        )
      } catch (e) {
        if (e.code === 'E_DOMAIN_UNLOADED') {
          let isLoading = loading[e.dname]
          if (!isLoading && !domains.data[e.dname].isGhost) {
            initDomain(e.dname, 'link')
          }
        } else {
          log.warn('cannot decode %o', request)
          //~ throw e
        }
      }
    }
    return null
  }

  isTabAvailable = ({namedParams, templatingcfg}) => {
    if (this.isLazyPending()) return true
    let {classname, tabname} = namedParams
    if (LEGACY_CLASSES.includes(classname)) {
      return true
    }
    return !!templatingcfg.classes[classname]?.tabs[tabname]
  }

  isLazyPending = () => this.props.lazy && !this.state.hovered

  render() {
    let {
      path, // open as external link if path is given
      request,
      cut,
      children,
      oldRequest,
      replacedQueryParams,
      domains,
      templatingcfgs,
      go,
      deploy,
      title,
      external,
      target,
      onClick, // custom click handling, request => customizedRequest (abort go() if customizedRequest === null)
      onClicked, // simple click callback with event param
      lazy,
      apiurl,
      // custom formatting
      style,
      className,
    } = this.props

    request = Link.mergeProps(request, this.props)
    let props = {
      className: clsx(className, cut && styles.cut),
      style,
      title,
      onMouseEnter: () => !this.state.hovered && this.setState({hovered: true}),
    }

    // if api is offline switch to <span> building instead of <a> building
    let disabled = Link.is_disabled(this.props)

    if (!path) {
      let onLinkClick = e => {
        // e.stopPropagation();
        e.preventDefault()
        callIfFunc(onClicked, e)
        let customizedRequest = onClick ? onClick(request) : request
        if (customizedRequest === undefined) {
          // onClick without result
          // BEWARE: connection a redux action will not trigger this case as it returns a action struct
          // we could detect this via checking {type, payload, meta} here
          go(request)
        } else if (customizedRequest) {
          go(customizedRequest)
        }
      }

      let p = ''
      try {
        var decoded = decodeGoLazy(
          this.isLazyPending(),
          request,
          oldRequest,
          replacedQueryParams,
          domains,
          templatingcfgs
        )
        p = decoded.pathname
      } catch (e) {
        if (e.code === 'E_DOMAIN_UNLOADED') {
          return (
            <span className={cut ? styles.cut : null} style={{...props.style, cursor: 'wait'}}>
              {children}
            </span>
          )
        } else {
          return (
            <span className={cut ? styles.cut : null} style={{color: 'red'}}>
              {children}
            </span>
          )
        }
      }

      // if target class/tab is not available in templatingcfg, switch to disabled (or mark?)
      disabled = disabled || !this.isTabAvailable(decoded)

      // build full url for href indicator
      if (request.keepQuery && oldRequest) {
        const str = map(oldRequest, (v, k) => `${k}=${encodeURIComponent(v)}`).join('&')
        if (str) {
          p += `?${str}`
        }
      } else if (size(decoded.query) > 0) {
        let str = map(decoded.query, (v, k) => `${k}=${encodeURIComponent(v)}`).join('&')
        p += `?${str}`
      }

      if (!external) {
        p = ensurePathPrefix(deploy.pathPrefix, p)
        props = {...props, href: p, onClick: onLinkClick}
      } else {
        // external link
        props = {...props, href: p.startsWith('/') ? apiurl.href(p) : p, target}
      }
    } else {
      // path given: external link
      props = {...props, href: path, target}
    }

    let {icon, basic, size: suiSize, floated, as: elementType, secondary} = this.props
    if (['button', 'label'].includes(elementType) || icon || basic) {
      let content = (
        <>
          {icon && <Icon name={icon} />}
          {children}
        </>
      )
      let E = {button: Button, label: Label}[elementType]
      return React.createElement(E, {
        ...props,
        floated,
        basic,
        secondary,
        compact: true,
        size: suiSize,
        children: content,
      })
    } else {
      if (disabled) {
        let {href, onClick, ...remaining} = props
        props = {...remaining, className: clsx(props.className, styles.disabledLink)}
      }
      return <a {...props}>{children}</a>
    }
  }
}

let ConnectedLink = connect(
  'Link',
  Link
)
export {ConnectedLink as Link}

export const UnknownDomainLink = ({children, ...props}) => {
  /* use these links only sparsly
  we should know the domain of a otid to keep the number of resolving calls low
  */
  let [active, setActive] = useState(false)
  let {data, error} = useApi('getDomainId', {otid: props.id}, {condition: active})

  if (!data) {
    return (
      <a href="#" onMouseEnter={() => setActive(true)}>
        {children}
      </a>
    )
  }
  return (
    <ConnectedLink odid={data.odid} {...props}>
      {children}
    </ConnectedLink>
  )
}

export const ApiLink = ({children, path, ...props}) => (
  <ConnectedLink path={useSelector(Sel.apiurl).href(path)} {...props}>
    {children}
  </ConnectedLink>
)

export const RawLink = ({children, linkIcon, target, ...props}) => {
  let user = useSelector(Sel.user)
  if (user.client == 'kiosk') {
    // no new tabs in kiosk mode
    target = undefined
  }
  return (
    <a target={target} {...props}>
      {children} {linkIcon && <VIcon style={{fontSize: '.75em'}} name="link" />}
    </a>
  )
}
